// THE report registry. Adding a report type is a definition file plus one line here — never a new
// page. This is the only place a report gets registered; the Live Reporting picker and the
// generic ReportView both drive off it.
//
// ═══ THE DEFINITION SHAPE ═══
//
//   {
//     id:          'exec_summary',        // stable; also the ?page= value the picker navigates to
//     title:       'Property Executive Summary',
//     description: 'One line for the picker tile.',
//     soon:        false,                 // true => tile renders a "Soon" badge and does not navigate
//
//     filters: ['client', 'years', 'status', 'state', 'property', 'basis'],
//              // which controls ReportView renders. Order is the order shown.
//
//     columns: [
//       { key, label, type, align?, total?, optional?, width? }
//     ],
//       // type     — one of COLUMN_TYPES in ./format ('text'|'money'|'value'|'int'|'status'|'year'|'date')
//       // align    — overrides the type default (numbers right, everything else left)
//       // total    — true: the footer sums this column
//       // optional — true: available but OFF by default, toggled by the user in the column picker
//
//     async fetch(ctx)          -> raw rows.    ctx = { clientIds, years, filters, signal }
//     buildRows(raw, ctx)       -> display rows, keyed by column.key. A cell may be a bare value
//                                  or { value, estimated } — see ./format.
//     summary(rows, ctx)        -> optional [{ label, value, sub }] block above the table
//     groupBy                   -> optional column key to group the table by
//     defaultSort               -> optional { key, dir: 'asc'|'desc' }
//   }
//
//   A CARD (step 8 part 6) is { id, title, description, component, adminOnly? }: Live Reporting renders `component`
//   ({ darkMode, staffUser, allClients, onDrillParcel }) under its title instead of ReportView — for a screen whose
//   interaction a table definition can't express (Notice Progress's drill-down). adminOnly hides it from everyone else.
//
// ⛔ fetch() MUST go through ./pagination (PostgREST truncates at 1000 rows silently) and MUST gate
// on ./represented (a report must never show a year a client wasn't ours). Rates go through
// ./taxRate — no inline rate arithmetic. These are not style preferences; each one is a bug that
// has already shipped once.
//
// ═══ REGISTERING A REPORT ═══
// Write ./definitions/<id>.js and add it to the array below. That is the whole procedure — no new
// page, no new route, no new tile. src/pages/PropertyExecutiveSummary.js was the last hand-built
// report page; it was deleted once its definition reconciled to the cent, because two routes to two
// implementations of one report is a demo hazard. It is in git history if the old code is ever wanted.

import propertyExecutiveSummary from './definitions/propertyExecutiveSummary';
import outstandingAppeals from './definitions/outstandingAppeals';
import portfolioTaxTrend from './definitions/portfolioTaxTrend';
import appealResultsSummary from './definitions/appealResultsSummary';
import jurisdictionDeadlines from './definitions/jurisdictionDeadlines';
import noticeProgress from './definitions/noticeProgress';
import productivity from './definitions/productivity';
import taxRates from './definitions/taxRates';

export const REPORTS = [propertyExecutiveSummary, outstandingAppeals, portfolioTaxTrend, appealResultsSummary, jurisdictionDeadlines,
  noticeProgress, productivity, taxRates];

export const getReport = (id) => REPORTS.find((r) => r.id === id) || null;

// Tiles for the Live Reporting picker: every registered definition, plus placeholders for report
// types that are named but not yet built. A placeholder is just a definition with `soon: true` and
// no fetch/columns, so the picker needs no special case. ⛔ A REGISTERED report gets NO placeholder
// — its title and description live in the definition, and a duplicate here would be a second home
// for the same value, free to drift.
export const PLACEHOLDERS = [
];

// What the picker renders: a registered definition wins over a placeholder of the same id, so a
// tile flips from "Soon" to live the moment its definition lands in REPORTS.
function pickerTiles() {
  const byId = new Map(PLACEHOLDERS.map((p) => [p.id, p]));
  REPORTS.forEach((r) => byId.set(r.id, { id: r.id, title: r.title, description: r.description, soon: !!r.soon, adminOnly: !!r.adminOnly }));
  return byId;
}

// Live Reporting's sections, in order (Philip 2026-10-21). A tile with no section here still shows, under "Other",
// so a newly registered report is never hidden by a forgotten line.
export const REPORT_SECTIONS = [
  { title: 'Savings & Appeals', ids: ['exec_summary', 'appeal_results', 'outstanding_appeals'] },
  { title: 'Taxes', ids: ['portfolio_trend', 'tax_rates'] },
  { title: 'Notices & Deadlines', ids: ['notice_progress', 'jurisdiction_deadlines'] },
  { title: 'Team', ids: ['productivity'] },
];
// isAdmin: an adminOnly tile (Productivity) is left out for everyone else.
export function pickerSections({ isAdmin = false } = {}) {
  const tiles = pickerTiles();
  if (!isAdmin) [...tiles.values()].filter((t) => t.adminOnly).forEach((t) => tiles.delete(t.id));
  const placed = new Set(REPORT_SECTIONS.flatMap((s) => s.ids));
  const sections = REPORT_SECTIONS.map((s) => ({ title: s.title, tiles: s.ids.map((id) => tiles.get(id)).filter(Boolean) }));
  const other = [...tiles.values()].filter((t) => !placed.has(t.id));
  return [...sections, ...(other.length ? [{ title: 'Other', tiles: other }] : [])].filter((s) => s.tiles.length);
}
